import { useCallback, useMemo, useState } from 'react';
import { Link } from '@/components/link';
import type { ShopperSearch } from '@/scapi';
import { createProductUrl, getImagesForColor } from '@/lib/product-utils';
import { useDynamicImageContext } from '@/providers/dynamic-image';
import { ProductImage } from './product-image';
import ImageNavArrows from '@/components/image-nav-arrows';
import { useTranslation } from 'react-i18next';
import placeholderImage from '/images/card-placeholder.jpg';
import type { SCAPIShopperSearchSchemas } from 'src/types';
import { resolveImageAlt } from '@/lib/images/resolve-image-alt';
import { PRODUCT_IMAGE_VIEW_TYPES } from '@/lib/product-image-view-types';
import { useConfig } from '@salesforce/storefront-next-runtime/config';

interface ProductImageContainerProps {
    product: SCAPIShopperSearchSchemas['ProductSearchHit'];
    selectedColorValue?: string | null;
    className?: string;
    handleProductClick?: (product: ShopperSearch.schemas['ProductSearchHit']) => void;
    /** Image aspect ratio (width/height). If provided, calculates height based on viewport width. Defaults to 1 (square) */
    imgAspectRatio?: number;
    /** Show prev/next navigation arrows when multiple images are available */
    showNavigationArrows?: boolean;
    /**
     * When `false`, the image is not wrapped in a PDP `Link` (e.g. the parent product tile is the hit target).
     * Default `true`.
     */
    pdpLink?: boolean;
    /**
     * Render the image with an empty alt. Use when the product name is already exposed next to it
     * (e.g. the tile heading link), so assistive technologies do not read it twice.
     */
    decorative?: boolean;
}

const ProductImageContainer = ({
    product,
    selectedColorValue = null,
    className,
    handleProductClick,
    imgAspectRatio = 1,
    showNavigationArrows = false,
    pdpLink = true,
    decorative = false,
}: ProductImageContainerProps) => {
    const { t } = useTranslation('product');
    const config = useConfig();
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const tileViewType = config.search?.products?.images?.tile ?? PRODUCT_IMAGE_VIEW_TYPES.SN;

    // Get all images for the selected color variant
    const allImages = useMemo(
        () => getImagesForColor(product, selectedColorValue, tileViewType),
        [product, selectedColorValue, tileViewType]
    );

    const currentImage = allImages[selectedImageIndex] ?? allImages[0] ?? product.image;
    const currentImageUrl = currentImage?.disBaseLink || currentImage?.link;
    const imageAltFallback = product.productName || t('imageAlt') || 'Product Image';
    // Local static fallback — not on SFCC/DIS; avoid webp/sw/q rewrite that 403s on the MRT bundle.
    const imageSrc = currentImageUrl || placeholderImage;
    const enableDis = Boolean(currentImageUrl);

    // Report the image URL to the dynamic image context, if available
    const imageContext = useDynamicImageContext();
    currentImageUrl && imageContext?.addSource(currentImageUrl);

    const handleClick = useCallback(() => {
        handleProductClick?.(product);
    }, [handleProductClick, product]);

    // When a non-square aspect ratio is requested, apply it via the native CSS
    // `aspect-ratio` property. The legacy padding-bottom percentage trick is not
    // used here because it conflicts with the native property and collapses the
    // image height to zero.
    const heightStyle = imgAspectRatio !== 1 ? { aspectRatio: `${imgAspectRatio}` } : {};

    const image = (
        <ProductImage
            src={imageSrc}
            alt={decorative ? '' : resolveImageAlt({ alt: currentImage?.alt, fallback: imageAltFallback })}
            className={
                pdpLink
                    ? 'w-full h-full object-contain transition-all duration-200 group-hover:scale-105'
                    : 'w-full h-full object-contain'
            }
            widths={enableDis ? imageContext?.widths : undefined}
            heights={enableDis ? imageContext?.heights : undefined}
            enableDis={enableDis}
        />
    );

    return (
        <div
            className={`${showNavigationArrows ? 'group/image ' : ''}relative overflow-hidden bg-secondary/20 flex flex-col ${
                imgAspectRatio === 1 ? 'aspect-square' : ''
            } ${className || ''}`}
            style={heightStyle}>
            {/* Product Image: optional PDP link when the host provides the main navigation surface */}
            {pdpLink ? (
                <Link
                    to={createProductUrl(product.c_url || '#', selectedColorValue)}
                    onClick={handleClick}
                    className="block w-full h-full flex-1"
                    aria-label={t('viewProductAriaLabel', { productName: imageAltFallback }) || imageAltFallback}>
                    {image}
                </Link>
            ) : (
                <div className="block w-full h-full flex-1">{image}</div>
            )}
            {/* Navigation Arrows - visible on hover */}
            {showNavigationArrows && allImages.length > 1 && (
                <ImageNavArrows
                    imageCount={allImages.length}
                    onIndexChange={setSelectedImageIndex}
                    className="opacity-0 group-hover/image:opacity-100 transition-opacity"
                />
            )}
        </div>
    );
};

export { ProductImageContainer };
